/**
 * The configuration rules every portal applies to a raw build-time value, each
 * stated ONCE.
 *
 * Owns: whether a Clerk publishable key is usable, whether a map token is a
 * PUBLIC one, and whether a build is a pilot build. Each takes the RAW value -
 * possibly undefined, possibly padded - and answers the usable value or its
 * absence; nothing here throws and nothing here guesses.
 *
 * Refuses: reading `import.meta.env`. Every `VITE_` read stays inside the app
 * that declares the variable, because `scripts/check-vite-vars.mjs` matches an
 * app's reads against its own `vite-env.d.ts` and its deploy steps - a read
 * moved into this package would be invisible to that gate. An app calls these
 * over its own read: `usablePublicMapToken(import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN)`.
 *
 * Before this file the Clerk rule lived in two apps with two suffix lengths, the
 * map-token rule in two apps byte for byte, and the pilot flag in two bodies.
 */

const CLERK_PUBLISHABLE_KEY = /^pk_(test|live)_[A-Za-z0-9+/=_-]{8,}$/;
const PUBLIC_MAP_TOKEN = /^pk\.[A-Za-z0-9._-]{20,}$/;

/**
 * The usable Clerk publishable key, trimmed, or null for an empty value, a
 * malformed one, a secret `sk_` key or the `pk_test_...` placeholder that ships
 * in `.env.example`.
 *
 * A publishable key is only usable when it actually looks like one. Handed to
 * Clerk, an empty string or a leftover placeholder fails at render time with an
 * opaque error, which during a 06:00 service is the worst possible moment to
 * discover a missing variable. Null lets the portal say which variable is
 * missing instead.
 */
export function usableClerkPublishableKey(raw: string | undefined): string | null {
  const value = (raw ?? '').trim();
  return CLERK_PUBLISHABLE_KEY.test(value) ? value : null;
}

/**
 * A PUBLIC map token, and only a public one, trimmed - or null.
 *
 * The worker's `MAPBOX_ACCESS_TOKEN` is a server secret and must never reach a
 * bundle: everything under `VITE_` is compiled into JavaScript any visitor can
 * read. So a browser map gets its own variable, and this rule refuses anything
 * that is not the publishable `pk.` form - a secret key pasted there by mistake
 * would otherwise be published on the next deploy, and the map would WORK, which
 * is the worst way for that to be discovered. The `.env.example` placeholder is
 * refused for the same reason the Clerk one is: absent configuration must
 * present as absent, not as an opaque failure inside a third-party library.
 */
export function usablePublicMapToken(raw: string | undefined): string | null {
  const value = (raw ?? '').trim();
  return PUBLIC_MAP_TOKEN.test(value) ? value : null;
}

/**
 * Whether `VITE_PILOT_MODE` builds this portal for the founder's pilot gate
 * (docs/pilot-access.html): the string `'1'` exactly, untrimmed.
 *
 * Declared explicitly rather than inferred from a missing key, so an ordinary
 * deployment that forgot its key still fails the way it always did instead of
 * waiting on a `cib_pilot` cookie nobody set.
 */
export function isPilotBuild(raw: string | undefined): boolean {
  return raw === '1';
}
